/**
 * 必打论据覆盖度（教师手动勾选）。
 *
 * 分母来自 motion 卡片上的必打论据（申论卡 mandatoryEvidences + 沙盘
 * mustHitEvidenceIds），分子为 RoomBackend.manualEvidenceChecks；
 * 教师逐条勾选会写回权威状态（toggleEvidence），并进入战报导出。
 */
import { SIDES, sideLabel, type MotionPackage, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState, cx } from '../ui/primitives.tsx';
import { buildEvidenceCoverage } from '../../export/report.ts';

function SideCoverage({ motion, side }: { motion: MotionPackage; side: Side }) {
  const { state, actions } = useArena();
  const coverage = buildEvidenceCoverage(motion, state.manualEvidenceChecks)[side];
  const checked = new Set(coverage.checkedEvidenceIds);

  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Badge tone={side === 'AFF' ? 'aff' : 'neg'}>{sideLabel(side)}</Badge>
          <span className="text-[11px] text-slate-400">
            {coverage.hitCount} / {coverage.requiredCount} 条必打
          </span>
        </div>
        <span
          className={cx(
            'mono text-xs',
            coverage.percent >= 80
              ? 'text-assert-pass'
              : coverage.percent >= 40
                ? 'text-clock-warning'
                : 'text-assert-violation',
          )}
          data-testid={`coverage-percent-${side.toLowerCase()}`}
        >
          {coverage.percent}%
        </span>
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/40">
        <div
          className={cx('h-full rounded-full', side === 'AFF' ? 'bg-team-aff' : 'bg-team-neg')}
          style={{ width: `${coverage.percent}%` }}
        />
      </div>
      {coverage.required.length === 0 ? (
        <p className="mt-2 text-[11px] text-slate-500">本题库未给该阵营配置必打论据。</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {coverage.required.map((item) => (
            <li key={item.evidenceId}>
              <label className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-0.5 text-[11px] hover:bg-white/5">
                <input
                  type="checkbox"
                  data-testid={`evidence-check-${side}-${item.evidenceId}`}
                  checked={checked.has(item.evidenceId)}
                  onChange={() => actions.toggleEvidence(side, item.evidenceId)}
                  className="mt-0.5 h-3 w-3 accent-current"
                />
                <span className="min-w-0">
                  <span
                    className={cx(
                      'block truncate font-medium',
                      checked.has(item.evidenceId) ? 'text-slate-200 line-through' : 'text-slate-300',
                    )}
                  >
                    {item.shortTitle}
                  </span>
                  <span className="block truncate text-slate-500">{item.exactQuoteOrStats}</span>
                  <span className="block truncate text-[10px] text-slate-600">
                    来源：{item.sources.join(' / ')}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EvidenceCoveragePanel({ motion }: { motion: MotionPackage }) {
  const { state } = useArena();
  const coverage = buildEvidenceCoverage(motion, state.manualEvidenceChecks);
  const totalRequired = coverage.AFF.requiredCount + coverage.NEG.requiredCount;
  const totalHit = coverage.AFF.hitCount + coverage.NEG.hitCount;

  return (
    <section className="rounded-xl border border-white/10 bg-bg-surface/80 backdrop-blur">
      <header className="flex items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">必打论据覆盖</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            手动勾选写入权威状态 · 合计 {totalHit} / {totalRequired} 条
          </p>
        </div>
        <span title="勾选结果会写回教师端权威状态，并随战报导出">☑</span>
      </header>
      <div className="space-y-2 p-3">
        {totalRequired === 0 ? (
          <EmptyState title="题库未配置必打论据" />
        ) : (
          SIDES.map((side) => <SideCoverage key={side} motion={motion} side={side} />)
        )}
      </div>
    </section>
  );
}
